import React, { useMemo, useState } from 'react';
import { Broadcast, CostCap, PricingModelOption } from '../../types';
import { formatNumber, formatCurrency, formatDate, formatCapRange, displayDateToYMD } from '../../utils/formatters';
import { modelHasValue } from '../../utils/pricing';
import './DataTable.css';

interface AggregatedRow {
  date: string;
  modified_at?: string;  // latest modification across the day's broadcasts (display only)
  donations: number;
  amount_raised: number;
  client_cost: number;
  client_roi: number;
  client_roi_percentage: number;
  pricing_model: string;
  // Admin-only fields
  sb_cost?: number;
  model_type?: string;
  model_value?: number;
  broadcastId?: string;        // original Broadcast.id for override key
  broadcast_date_id?: string;  // for persisting overrides
  account?: string;
  delivered?: number;
  hidden?: boolean;            // admin-only: row is hidden from clients
  uncapped_cost: number;       // admin-only: cost before cost caps (= client_cost when uncapped)
  cost_cap_ids: number[];      // admin-only: caps covering this day
}

// A day counts as capped only when a cap actually lowered its cost.
const isCapped = (row: AggregatedRow) =>
  row.cost_cap_ids.length > 0 && row.uncapped_cost - row.client_cost > 0.005;

interface DataTableProps {
  data: Broadcast[];
  loading?: boolean;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  pricingModelOptions?: PricingModelOption[];
  onPricingModelChange?: (broadcastId: string, modelType: string, modelValue: number) => void;
  onSavePricingModel?: (broadcastId: string) => Promise<void>;
  onResetPricingModel?: (broadcastId: string) => Promise<void>;
  onHideDates?: (dates: string[]) => Promise<void>;
  onUnhideDates?: (dates: string[]) => Promise<void>;
  costCaps?: CostCap[];
  onCapDates?: (startDate: string, endDate: string) => void;  // YYYY-MM-DD range
}

export const DataTable: React.FC<DataTableProps> = ({
  data,
  loading,
  isAdmin,
  isSuperAdmin,
  pricingModelOptions = [],
  onPricingModelChange,
  onSavePricingModel,
  onResetPricingModel,
  onHideDates,
  onUnhideDates,
  costCaps = [],
  onCapDates,
}) => {
  // Track which rows have an open value editor
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [savingRow, setSavingRow] = useState<string | null>(null);
  const [resettingRow, setResettingRow] = useState<string | null>(null);
  // Admin row selection (keyed by display date) for bulk hide/unhide
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [hideBusy, setHideBusy] = useState(false);

  // Aggregate by date for both admin and non-admin
  const aggregatedData = useMemo((): AggregatedRow[] => {
    const dateMap = new Map<string, AggregatedRow>();

    data.forEach((row) => {
      const dateKey = formatDate(row.created_at);
      const existing = dateMap.get(dateKey);

      if (existing) {
        existing.donations += row.donations || 0;
        existing.amount_raised += row.amount_raised || 0;
        existing.client_cost += row.client_cost || 0;
        if (isSuperAdmin) {
          existing.sb_cost = (existing.sb_cost || 0) + (row.sb_cost || 0);
        }
        existing.delivered = (existing.delivered || 0) + (row.delivered || 0);
        existing.hidden = existing.hidden || row.hidden;
        existing.uncapped_cost += row.uncapped_cost ?? row.client_cost ?? 0;
        (row.cost_cap_ids || []).forEach(id => {
          if (!existing.cost_cap_ids.includes(id)) existing.cost_cap_ids.push(id);
        });
        // Keep the latest modification across the day's broadcasts (display only).
        if (row.modified_at && (!existing.modified_at || row.modified_at > existing.modified_at)) {
          existing.modified_at = row.modified_at;
        }
      } else {
        dateMap.set(dateKey, {
          date: dateKey,
          modified_at: row.modified_at,
          donations: row.donations || 0,
          amount_raised: row.amount_raised || 0,
          client_cost: row.client_cost || 0,
          client_roi: 0,
          client_roi_percentage: 0,
          pricing_model: row.pricing_model || 'fixed_cost',
          model_type: row.model_type,
          model_value: row.model_value,
          delivered: row.delivered || 0,
          hidden: row.hidden,
          uncapped_cost: row.uncapped_cost ?? row.client_cost ?? 0,
          cost_cap_ids: [...(row.cost_cap_ids || [])],
          ...(isSuperAdmin ? { sb_cost: row.sb_cost || 0 } : {}),
          ...(isAdmin ? {
            broadcastId: row.id,
            broadcast_date_id: row.broadcast_date_id,
            account: row.account,
          } : {}),
        });
      }
    });

    dateMap.forEach((row) => {
      if (row.model_type === 'roi_guarantee' && row.model_value && row.model_value > 0 && !isCapped(row)) {
        // Trust the per-broadcast client_cost already summed into this row.
        // A capped day falls through to the real ROI (raised / capped cost).
        row.client_roi_percentage = row.model_value;
      } else if (row.model_type === 'profit_share') {
        // Profit share: client never pays more than they raised
        if (row.amount_raised > 0) {
          row.client_cost = Math.min(row.client_cost, row.amount_raised);
        }
        row.client_roi_percentage = row.client_cost > 0
          ? (row.amount_raised / row.client_cost) * 100
          : 0;
      } else {
        row.client_roi_percentage = row.client_cost > 0
          ? (row.amount_raised / row.client_cost) * 100
          : 0;
      }
      row.client_roi = row.amount_raised - row.client_cost;
    });

    return Array.from(dateMap.values()).sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [data, isAdmin, isSuperAdmin]);

  const handleModelTypeChange = (row: AggregatedRow, newModelType: string) => {
    if (!onPricingModelChange || !row.broadcastId) return;
    const option = pricingModelOptions.find(o => o.model_type === newModelType);
    const defaultValue = option?.default_value ?? 0;
    const value = row.model_type === newModelType
      ? (row.model_value ?? defaultValue)
      : defaultValue;
    onPricingModelChange(row.broadcastId, newModelType, value);
  };

  const handleValueSubmit = (row: AggregatedRow) => {
    if (!onPricingModelChange || !row.model_type || !row.broadcastId) return;
    const numValue = parseFloat(editValue);
    if (isNaN(numValue)) return;
    onPricingModelChange(row.broadcastId, row.model_type, numValue);
    setEditingRow(null);
  };

  const toggleRow = (date: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  };

  const allSelected = aggregatedData.length > 0 && selected.size === aggregatedData.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(aggregatedData.map(r => r.date)));
  };

  // Whether every selected row is currently hidden — decides the button's action.
  const selectedRows = aggregatedData.filter(r => selected.has(r.date));
  const allSelectedHidden = selectedRows.length > 0 && selectedRows.every(r => r.hidden);

  const capsById = useMemo(() => new Map(costCaps.map(c => [c.id, c])), [costCaps]);

  // "Cap selected": hand the selected days' first-to-last range to the caps
  // panel, which opens its add form with those dates (editable there).
  const capSelected = () => {
    if (!onCapDates || selected.size === 0) return;
    const ymds = Array.from(selected).map(displayDateToYMD).sort();
    onCapDates(ymds[0], ymds[ymds.length - 1]);
    setSelected(new Set());
  };

  const runHideAction = async (hide: boolean) => {
    const dates = Array.from(selected);
    if (dates.length === 0) return;
    const fn = hide ? onHideDates : onUnhideDates;
    if (!fn) return;
    setHideBusy(true);
    try {
      await fn(dates);
      setSelected(new Set());
    } finally {
      setHideBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="table-container">
        <div className="table-header">
          <h2>Broadcast Details</h2>
        </div>
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="table-container">
        <div className="table-header">
          <h2>Broadcast Details</h2>
        </div>
        <div className="loading">
          <p>No broadcasts found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="table-container">
      <div className="table-header">
        <h2>Broadcast Details</h2>
        {isAdmin && (
          <div className="hide-actions">
            <span className="hide-actions-count">
              {selected.size > 0 ? `${selected.size} selected` : 'Select rows to cap or hide'}
            </span>
            {onCapDates && (
              <button
                className="btn btn-secondary"
                disabled={selected.size === 0 || hideBusy}
                onClick={capSelected}
                title="Add a cost cap covering the first to last selected day"
              >
                Cap selected
              </button>
            )}
            <button
              className="btn btn-secondary"
              disabled={selected.size === 0 || hideBusy}
              onClick={() => runHideAction(!allSelectedHidden)}
              title="Hidden rows disappear entirely for client (non-admin) users"
            >
              {hideBusy ? '...' : allSelectedHidden ? 'Unhide selected' : 'Hide selected'}
            </button>
          </div>
        )}
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              {isAdmin && (
                <th className="hide-col">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              <th>Date</th>
              <th>Donations</th>
              <th>Amount Raised</th>
              {isSuperAdmin && <th>Cost Estimate</th>}
              <th>Your Cost</th>
              {isAdmin && <th>Cap</th>}
              <th>Your ROI</th>
              <th>Your Model</th>
            </tr>
          </thead>
          <tbody>
            {aggregatedData.map((row) => (
              <tr key={row.date} className={row.hidden ? 'row-hidden' : undefined}>
                {isAdmin && (
                  <td className="hide-col">
                    <input
                      type="checkbox"
                      checked={selected.has(row.date)}
                      onChange={() => toggleRow(row.date)}
                      aria-label={`Select ${row.date}`}
                    />
                  </td>
                )}
                <td>{(() => {
                  // Show "created - modified" when the broadcast was modified on a
                  // different calendar day; otherwise just the created date. Purely
                  // cosmetic — row.date (created) remains the key for all logic.
                  const modified = row.modified_at ? formatDate(row.modified_at) : null;
                  const label = modified && modified !== row.date
                    ? `${row.date} - ${modified}`
                    : row.date;
                  return (
                    <>
                      {label}
                      {isAdmin && row.hidden && <span className="hidden-badge">Hidden</span>}
                    </>
                  );
                })()}</td>
                <td>{formatNumber(row.donations)}</td>
                <td>{formatCurrency(row.amount_raised)}</td>
                {isSuperAdmin && <td>{formatCurrency(row.sb_cost ?? 0)}</td>}
                <td>{formatCurrency(row.client_cost)}</td>
                {isAdmin && (
                  <td className="cap-cell">
                    {row.cost_cap_ids.length === 0 ? (
                      <span className="cap-none">–</span>
                    ) : (
                      <>
                        {row.cost_cap_ids.map(id => {
                          const cap = capsById.get(id);
                          return (
                            <span key={id} className="cap-tag">
                              {cap
                                ? `${formatCapRange(cap.start_date, cap.end_date)} · ${formatCurrency(cap.cap_amount)}`
                                : 'Cap'}
                            </span>
                          );
                        })}
                        {isCapped(row) && (
                          <span className="cap-was">was {formatCurrency(row.uncapped_cost)}</span>
                        )}
                      </>
                    )}
                  </td>
                )}
                <td className={
                  // A day the cap brought to $0 shows 0% ROI; keep it neutral
                  // rather than red, since nothing went wrong for the client.
                  isCapped(row) && row.client_cost === 0
                    ? undefined
                    : row.client_roi_percentage >= (row.model_type === 'roi_guarantee' && row.model_value ? row.model_value : 100) ? 'positive' : 'negative'
                }>
                  {row.client_roi_percentage.toFixed(2)}%
                </td>
                <td>
                  {isAdmin && row.broadcastId ? (
                    <div className="pricing-controls">
                      <select
                        className="pricing-select"
                        value={row.model_type || ''}
                        onChange={(e) => handleModelTypeChange(row, e.target.value)}
                      >
                        {pricingModelOptions.map(opt => (
                          <option key={opt.model_type} value={opt.model_type}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {modelHasValue(row.model_type) && (
                        editingRow === row.date ? (
                          <input
                            className="pricing-value-input"
                            type="number"
                            step="any"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => handleValueSubmit(row)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleValueSubmit(row);
                              if (e.key === 'Escape') setEditingRow(null);
                            }}
                            autoFocus
                          />
                        ) : (
                          <button
                            className="pricing-value-btn"
                            onClick={() => {
                              setEditingRow(row.date);
                              setEditValue(String(row.model_value ?? 0));
                            }}
                          >
                            {row.model_value ?? 0}
                          </button>
                        )
                      )}
                      <button
                        className="pricing-save-btn"
                        disabled={savingRow === row.date}
                        onClick={async () => {
                          if (!onSavePricingModel || !row.broadcastId) return;
                          setSavingRow(row.date);
                          try { await onSavePricingModel(row.broadcastId); }
                          finally { setSavingRow(null); }
                        }}
                      >
                        {savingRow === row.date ? '...' : 'Save'}
                      </button>
                      <button
                        className="pricing-reset-btn"
                        disabled={resettingRow === row.date}
                        onClick={async () => {
                          if (!onResetPricingModel || !row.broadcastId) return;
                          setResettingRow(row.date);
                          try { await onResetPricingModel(row.broadcastId); }
                          finally { setResettingRow(null); }
                        }}
                      >
                        {resettingRow === row.date ? '...' : 'Reset'}
                      </button>
                    </div>
                  ) : (
                    row.pricing_model
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
